import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { watchlistApi } from "@/api/ux";
import { Card } from "@/design-system/components/ui";
import { relativeTime } from "@/components/ux/relativeTime";
import {
  entityTypeLabel,
  isCapitalMarketsEntity,
  resolveCapitalMarketsRoute,
} from "./cmEntities";

/**
 * SPV Pool v1.0 §4.7 / PR 9 — Watchlist widget scoped to the Capital Markets
 * module. Reads the unified watchlist and filters to SPV / Pool / Investor /
 * service-provider entity types so no Broker / Deal / Loan items leak in.
 */
export function CapitalMarketsWatchlist() {
  const list = useQuery({
    queryKey: ["watchlist", "all"],
    queryFn: () => watchlistApi.list(),
  });

  const grouped = useMemo(() => {
    const rows = (list.data ?? []).filter((r) =>
      isCapitalMarketsEntity(r.entityType),
    );
    const groups = new Map<string, typeof rows>();
    for (const row of rows) {
      const existing = groups.get(row.entityType) ?? [];
      existing.push(row);
      groups.set(row.entityType, existing);
    }
    return [...groups.entries()].map(([type, items]) => ({ type, items }));
  }, [list.data]);

  return (
    <Card>
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
        Watching
      </div>
      <div className="font-display text-lead font-bold mb-space-3">
        Capital Markets watchlist
      </div>

      {list.isLoading && (
        <div className="text-label uppercase tracking-label text-gray-mid">
          Loading…
        </div>
      )}

      {!list.isLoading && grouped.length === 0 && (
        <div
          className="flex flex-col items-center text-center py-space-4"
          data-testid="watchlist-empty"
        >
          <Star
            size={28}
            strokeWidth={1.5}
            className="text-gold-shadow mb-space-2"
            aria-hidden
          />
          <p className="text-small text-gray-mid m-0 max-w-reading">
            Star any pool, trust, investor, or service provider to add to your
            watchlist.
          </p>
        </div>
      )}

      {!list.isLoading && grouped.length > 0 && (
        <div className="flex flex-col gap-space-3">
          {grouped.map((group) => (
            <div key={group.type}>
              <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-1">
                {entityTypeLabel(group.type)}
              </div>
              <ul className="m-0 p-0 list-none flex flex-col">
                {group.items.map((item) => (
                  <li
                    key={item.id}
                    className="border-t border-border-light first:border-t-0"
                  >
                    <Link
                      to={resolveCapitalMarketsRoute(
                        item.entityType,
                        item.entityId,
                      )}
                      className="flex items-center justify-between gap-space-2 py-space-2 text-small text-onyx hover:bg-gold-highlight/10 rounded-sm px-space-2 -mx-space-2"
                    >
                      <span className="truncate">
                        {item.entityTitle ??
                          `${item.entityType} ${item.entityId}`}
                      </span>
                      <span className="flex items-center gap-space-2 shrink-0">
                        <span className="text-label text-gray-mid">
                          {relativeTime(item.createdAt)}
                        </span>
                        <ChevronRight
                          size={14}
                          strokeWidth={1.5}
                          className="text-gray-mid"
                          aria-hidden
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
